import React, { useState, useRef, useEffect, useCallback } from 'react';
import { YoutubeManager } from '../utils/YoutubeManager';

export interface VideoArchiveFilters {
    dateStart: string;
    dateEnd: string;
    keywords: string;
    freeText: string;
}

const defaultFilters: VideoArchiveFilters = {
    dateStart: '',
    dateEnd: '',
    keywords: '',
    freeText: ''
};

interface VideoArchiveFilterProps {
    filters: VideoArchiveFilters;
    onFiltersChange: (filters: VideoArchiveFilters) => void;
    onSearch: (filters: VideoArchiveFilters) => void;
    loading?: boolean;
    /** If provided, used for keyword autocomplete instead of fetching tags. */
    initialTags?: string[];
}

/** Returns the current token for autocomplete (segment after last comma, trimmed). */
function getCurrentKeywordToken(keywords: string): string {
    const lastComma = keywords.lastIndexOf(',');
    const segment = lastComma === -1 ? keywords : keywords.slice(lastComma + 1);
    return segment.trim();
}

/** Returns keywords string with the current token replaced by the selected tag. */
function replaceCurrentToken(keywords: string, selectedTag: string): string {
    const lastComma = keywords.lastIndexOf(',');
    if (lastComma === -1) return selectedTag;
    const before = keywords.slice(0, lastComma).trim();
    return before ? `${before}, ${selectedTag}` : selectedTag;
}

function VideoArchiveFilter({
    filters,
    onFiltersChange,
    onSearch,
    loading = false,
    initialTags
}: VideoArchiveFilterProps) {
    const [allTags, setAllTags] = useState<string[]>([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const keywordInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (initialTags?.length) {
            setAllTags(initialTags);
        }
    }, [initialTags]);

    const loadTags = useCallback(async () => {
        if (allTags.length > 0 || tagsLoading) return;
        if (initialTags?.length) return;
        setTagsLoading(true);
        try {
            const tagCounts = await YoutubeManager.getInstance().fetchChannelVideoTagCounts();
            const tags = tagCounts.map((t) => t.tag);
            setAllTags(tags);
        } catch {
            setAllTags([]);
        } finally {
            setTagsLoading(false);
        }
    }, [allTags.length, tagsLoading, initialTags?.length]);

    const currentToken = getCurrentKeywordToken(filters.keywords);
    const matchingTags = currentToken
        ? allTags.filter((tag) => tag.toLowerCase().startsWith(currentToken.toLowerCase()))
        : [];

    const showDropdown = showSuggestions && matchingTags.length > 0;

    const handleKeywordFocus = () => {
        if (blurTimerRef.current) {
            clearTimeout(blurTimerRef.current);
            blurTimerRef.current = null;
        }
        setShowSuggestions(true);
        loadTags();
    };

    const handleKeywordBlur = () => {
        blurTimerRef.current = setTimeout(() => setShowSuggestions(false), 150);
    };

    const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onFiltersChange({ ...filters, keywords: e.target.value });
        setShowSuggestions(true);
    };

    const handleSelectTag = (tag: string) => {
        onFiltersChange({
            ...filters,
            keywords: replaceCurrentToken(filters.keywords, tag)
        });
        setShowSuggestions(false);
        keywordInputRef.current?.focus();
    };

    useEffect(() => {
        return () => {
            if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
        };
    }, []);

    const handleChange = (field: keyof VideoArchiveFilters) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        onFiltersChange({ ...filters, [field]: e.target.value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSearch(filters);
    };

    const handleReset = () => {
        onFiltersChange(defaultFilters);
        onSearch(defaultFilters);
    };

    return (
        <div className="video-archive-filter card border-0 shadow-sm">
            <div className="card-body p-4">
                <h5 className="card-title mb-3">Filter videos</h5>
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label htmlFor="video-filter-datestart" className="form-label small text-muted">
                            Start date
                        </label>
                        <input
                            id="video-filter-datestart"
                            type="date"
                            className="form-control form-control-sm"
                            value={filters.dateStart}
                            onChange={handleChange('dateStart')}
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="video-filter-dateend" className="form-label small text-muted">
                            End date
                        </label>
                        <input
                            id="video-filter-dateend"
                            type="date"
                            className="form-control form-control-sm"
                            value={filters.dateEnd}
                            onChange={handleChange('dateEnd')}
                        />
                    </div>
                    <div className="mb-3 position-relative">
                        <label htmlFor="video-filter-keywords" className="form-label small text-muted">
                            Keywords
                        </label>
                        <input
                            ref={keywordInputRef}
                            id="video-filter-keywords"
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g. SharePoint, Power Apps"
                            value={filters.keywords}
                            onChange={handleKeywordChange}
                            onFocus={handleKeywordFocus}
                            onBlur={handleKeywordBlur}
                            autoComplete="off"
                            aria-autocomplete="list"                            
                            aria-controls="video-keyword-suggestions"
                        />
                        {showDropdown && (
                            <ul
                                id="video-keyword-suggestions"
                                className="video-keyword-suggestions list-unstyled border shadow-sm rounded mt-1"
                                role="listbox"
                            >
                                {matchingTags.map((tag) => (
                                    <li
                                        key={tag}
                                        role="option"
                                        aria-selected={false}
                                        className="video-keyword-suggestion-item px-2 py-1 small cursor-pointer"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleSelectTag(tag);
                                        }}
                                    >
                                        {tag}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {tagsLoading && allTags.length === 0 && (
                            <small className="text-muted">Loading keywords…</small>
                        )}
                        <small className="text-muted d-block">Comma-separated</small>
                    </div>
                    <div className="mb-4">
                        <label htmlFor="video-filter-freetext" className="form-label small text-muted">
                            Free text search
                        </label>
                        <input
                            id="video-filter-freetext"
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search in title and description"
                            value={filters.freeText}
                            onChange={handleChange('freeText')}
                        />
                    </div>
                    <div className="d-flex flex-column gap-2">
                        <button
                            type="submit"
                            className="btn btn-primary btn-sm"
                            disabled={loading}
                        >
                            {loading ? 'Searching…' : 'Search'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleReset}
                            disabled={loading}
                        >
                            Reset
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default VideoArchiveFilter;
